import { useMemo, useState } from 'react';
import { SortDescriptor } from '@ds';
import { CandidatesTable } from '@lp/features/AssignmentTables/components/CandidatesTable/CandidatesTable';
import { CONTRACTOR_SURGEON_COLUMNS } from '../config';
import { AffiliationType, RSContractorAssignmentCandidate } from '@/api/apis/resources';
import { SurgeonTableBaseProps } from '../types';
import { searchContractorSurgeons } from '../utils';
import { useContractorSurgeonsForAssignment } from '@/queries/resource';
import { renderContractorSurgeonTableCell } from './renderContractorSurgeonTableCell';
import { mapSelected, sortCandidates } from '../../utils';

type ContractorSurgeonsTableProps = SurgeonTableBaseProps & {
  affiliation?: AffiliationType;
  onReserve: (surgeon: RSContractorAssignmentCandidate) => void;
};

export const ContractorSurgeonsTable = ({
  searchTerm,
  caseId,
  caseName,
  orTime,
  organ,
  donor,
  isLogistics,
  selectedSurgeonsIds,
  onReserve,
  affiliation,
}: ContractorSurgeonsTableProps) => {
  const [sort, setSort] = useState<SortDescriptor | undefined>();
  const { data: surgeons, isLoading } = useContractorSurgeonsForAssignment({ caseId, donor, organ, orTime });

  const tableRows = useMemo(() => {
    const searchResults = searchContractorSurgeons(searchTerm, surgeons);
    let filtered = searchResults;
    if (affiliation)
      filtered = searchResults.filter((surgeon) => surgeon.affiliation && surgeon.affiliation.type === affiliation);
    const mappedSurgeons = mapSelected(selectedSurgeonsIds, filtered, 'employeeId');

    return sortCandidates<RSContractorAssignmentCandidate>(mappedSurgeons, sort);
  }, [sort, searchTerm, surgeons, affiliation, selectedSurgeonsIds]);

  const renderCell = useMemo(
    () => renderContractorSurgeonTableCell(isLogistics, donor, onReserve, caseName),
    [onReserve, isLogistics, donor, caseName],
  );

  return (
    <CandidatesTable
      label="Retrieval surgeons for assignment"
      columns={CONTRACTOR_SURGEON_COLUMNS}
      renderCell={renderCell}
      data={tableRows}
      isLoading={false}
      isFetching={isLoading}
      sortBy={sort}
      onSortByChange={setSort}
    />
  );
};
